/**
 * EMZI NEXUS V6.3 - Content Studio V2
 * Step 0: Platform & Duration Selection (Slider-Based UX)
 * 
 * Features:
 * - Slider control (5-180s, step 5s, snap points)
 * - Platform compatibility logic with dynamic badges
 * - Quick chips for common durations
 * - Apply button to trigger AI tips
 * - AI tips display after Apply (not real-time)
 * - EMZI Care CI design system
 */

import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { EMZI_COLORS, EMZI_SPACING, EMZI_RADIUS, EMZI_SHADOWS, EMZI_BUTTON_STYLES } from '../../../styles/emziCI';
import { withProgress } from '../../../utils/withProgress';
import { secureStorage } from '../../../utils/secureStorage';
import { BACKEND_URL } from '../../../utils/backendUrl';

const PLATFORMS = [
  { 
    id: 'TikTok', 
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
      </svg>
    )
  },
  { 
    id: 'Instagram',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
        <circle cx="12" cy="12" r="4"/>
        <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor"/>
      </svg>
    )
  },
  { 
    id: 'Facebook',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    )
  },
  { 
    id: 'YouTube',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    )
  },
  { 
    id: 'Landing Page',
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
        <line x1="8" y1="21" x2="16" y2="21"/>
        <line x1="12" y1="17" x2="12" y2="21"/>
      </svg>
    )
  }
];

// Platform duration rules
const PLATFORM_RULES = {
  'TikTok': { min: 15, max: 60, softMax: 90 },
  'Instagram': { min: 30, max: 60, softMax: 90 },
  'YouTube': { min: 15, max: 60, softMax: 60 },
  'Facebook': { min: 30, max: 120, softMax: 120 },
  'Landing Page': { min: 60, max: 180, softMax: 180 }
};

// Quick action chips
const QUICK_CHIPS = [
  { label: '15s', seconds: 15 },
  { label: '30s', seconds: 30 },
  { label: '45s', seconds: 45 },
  { label: '60s', seconds: 60 },
  { label: '90s', seconds: 90 },
  { label: '2m', seconds: 120 },
  { label: '3m', seconds: 180 }
];

// Snap points for slider
const SNAP_POINTS = [15, 30, 45, 60, 90, 120];

const Step0PlatformDuration = ({ sessionId, sessionData, onComplete }) => {
  const { language, t } = useLanguage();
  
  const [selectedChannels, setSelectedChannels] = useState(() => {
    return sessionData?.platformContext?.channels || [];
  });
  
  const [durationSeconds, setDurationSeconds] = useState(() => {
    return sessionData?.platformContext?.normalizedSeconds || 30;
  });
  
  const [errors, setErrors] = useState({});
  const [aiTips, setAiTips] = useState(null);  // null = not fetched yet
  const [compatibilityInfo, setCompatibilityInfo] = useState({ compatible: [], incompatible: [] });

  // Format seconds to human-readable (e.g., "1m 30s")
  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  // Calculate compatibility when platforms or duration changes
  useEffect(() => {
    if (selectedChannels.length === 0) {
      setCompatibilityInfo({ compatible: [], incompatible: [] });
      return;
    }

    const compatible = [];
    const incompatible = [];

    selectedChannels.forEach(platform => {
      const rules = PLATFORM_RULES[platform];
      if (!rules) {
        compatible.push(platform);
        return;
      }

      if (durationSeconds <= rules.softMax) {
        compatible.push(platform);
      } else {
        incompatible.push({
          platform,
          issue: `Max ${rules.softMax}s`,
          suggestion: `Try ≤${rules.softMax}s`
        });
      }
    });

    setCompatibilityInfo({ compatible, incompatible });
  }, [selectedChannels, durationSeconds]);

  // Snap to nearest snap point if close
  const snapToPoint = (value) => {
    const threshold = 3; // 3 seconds tolerance
    for (const point of SNAP_POINTS) {
      if (Math.abs(value - point) <= threshold) {
        return point;
      }
    }
    return value;
  };

  const handleSliderChange = (e) => {
    let value = parseInt(e.target.value);
    value = snapToPoint(value);
    setDurationSeconds(value);
    setErrors(prev => ({ ...prev, duration: null }));
    // Clear AI tips when slider changes
    setAiTips(null);
  };

  const handleQuickChip = (seconds) => {
    setDurationSeconds(seconds);
    setErrors(prev => ({ ...prev, duration: null }));
    // Clear AI tips when chip is clicked
    setAiTips(null);
  };

  const toggleChannel = (channelId) => {
    setSelectedChannels(prev => {
      if (prev.includes(channelId)) {
        return prev.filter(c => c !== channelId);
      } else {
        return [...prev, channelId];
      }
    });
    setErrors(prev => ({ ...prev, channels: null }));
    // Clear AI tips when platforms change
    setAiTips(null);
  };

  const handleApply = async () => {
    // Validate
    if (selectedChannels.length === 0) {
      setErrors({ channels: 'Please select at least one platform' });
      return;
    }

    // Fetch AI tips
    try {
      const result = await withProgress(
        {
          label: 'Analyzing duration',
          phases: ['Checking platform compatibility', 'Fetching AI tips'],
          canCancel: true,
          timeoutMs: 15000
        },
        async (signal) => {
          const response = await fetch(
            `${BACKEND_URL}/api/v2/content/duration/tips`,
            {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
              },
              body: JSON.stringify({
                channel_list: selectedChannels,
                duration_seconds: durationSeconds,
                session_id: sessionId,
                locale: language === 'bm' ? 'ms' : 'en'
              }),
              signal
            }
          );

          if (!response.ok) {
            throw new Error('Failed to fetch AI tips');
          }

          return await response.json();
        }
      );

      setAiTips(result);
      setErrors({});
    } catch (error) {
      console.error('Error fetching AI tips:', error);
      if (error?.name !== 'AbortError') {
        setErrors({ submit: 'Failed to fetch AI tips. Please try again.' });
      }
    }
  };

  const handleNext = async () => {
    // Validate
    if (selectedChannels.length === 0) {
      setErrors({ channels: 'Please select at least one platform' });
      return;
    }

    try {
      await withProgress(
        {
          label: 'Saving platform context',
          phases: ['Validating', 'Saving'],
          canCancel: false,
          timeoutMs: 10000
        },
        async (signal) => {
          const response = await fetch(
            `${BACKEND_URL}/api/v2/content/session/platform-context`,
            {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`
              },
              body: JSON.stringify({
                session_id: sessionId,
                channels: selectedChannels,
                duration: formatDuration(durationSeconds)
              }),
              signal
            }
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to save platform context');
          }

          const data = await response.json();

          // Update session data with platform context
          const platformContext = {
            channels: selectedChannels,
            duration: formatDuration(durationSeconds),
            normalizedSeconds: durationSeconds,
            constraints: data.platform_constraints
          };

          onComplete({ platformContext });
        }
      );
    } catch (error) {
      console.error('Error saving platform context:', error);
      if (error?.name !== 'AbortError') {
        setErrors({ submit: error.message });
      }
    }
  };

  const getText = (key) => {
    const translations = {
      en: {
        title: 'Select Platforms & Duration',
        subtitle: 'Choose your target channels and content duration',
        platformsLabel: 'Target Platforms',
        platformsHint: 'Select at least one platform',
        durationLabel: 'Target Duration',
        durationHint: 'Drag slider or click quick actions',
        applyButton: 'Get AI Tips',
        nextButton: 'Continue to Objective',
        compatibilityTitle: 'Platform Compatibility',
        compatibleWith: 'Compatible with',
        incompatibleWith: 'Not compatible',
        aiTipsTitle: 'AI Best Duration Tips'
      },
      bm: {
        title: 'Pilih Platform & Tempoh',
        subtitle: 'Pilih saluran sasaran dan tempoh kandungan anda',
        platformsLabel: 'Platform Sasaran',
        platformsHint: 'Pilih sekurang-kurangnya satu platform',
        durationLabel: 'Tempoh Sasaran',
        durationHint: 'Seret slider atau klik tindakan pantas',
        applyButton: 'Dapatkan Tip AI',
        nextButton: 'Teruskan ke Objektif',
        compatibilityTitle: 'Keserasian Platform',
        compatibleWith: 'Serasi dengan',
        incompatibleWith: 'Tidak serasi',
        aiTipsTitle: 'Tip Tempoh Terbaik AI'
      },
      bm_urban: {
        title: 'Pilih Platform & Duration',
        subtitle: 'Choose target channels dan content duration',
        platformsLabel: 'Target Platforms',
        platformsHint: 'Pilih at least satu platform',
        durationLabel: 'Target Duration',
        durationHint: 'Drag slider atau click quick actions',
        applyButton: 'Get AI Tips',
        nextButton: 'Continue ke Objective',
        compatibilityTitle: 'Platform Compatibility',
        compatibleWith: 'Compatible dengan',
        incompatibleWith: 'Not compatible',
        aiTipsTitle: 'AI Best Duration Tips'
      },
      urban: {
        title: 'Pilih Platform & Duration',
        subtitle: 'Choose target channels dan content duration',
        platformsLabel: 'Target Platforms',
        platformsHint: 'Pilih at least satu platform',
        durationLabel: 'Target Duration',
        durationHint: 'Drag slider atau click quick actions',
        applyButton: 'Get AI Tips',
        nextButton: 'Continue ke Objective',
        compatibilityTitle: 'Platform Compatibility',
        compatibleWith: 'Compatible dengan',
        incompatibleWith: 'Not compatible',
        aiTipsTitle: 'AI Best Duration Tips'
      }
    };
    
    return translations[language]?.[key] || translations.en[key];
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: EMZI_COLORS.surface,
      padding: EMZI_SPACING.lg
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: EMZI_SPACING.xxl, textAlign: 'center' }}>
          <h1 style={{ 
            fontSize: '22px',
            fontWeight: '700',
            marginBottom: EMZI_SPACING.sm,
            color: EMZI_COLORS.textPrimary,
            lineHeight: '1.2'
          }}>
            {getText('title')}
          </h1>
          <p style={{ 
            fontSize: '14px',
            color: EMZI_COLORS.textSecondary,
            fontWeight: '400'
          }}>
            {getText('subtitle')}
          </p>
        </div>

        {/* Platform Selection Card */}
        <Card 
          className="mb-4"
          style={{
            backgroundColor: EMZI_COLORS.background,
            borderRadius: EMZI_RADIUS.md,
            boxShadow: EMZI_SHADOWS.card,
            border: errors.channels ? `2px solid ${EMZI_COLORS.error}` : `1px solid ${EMZI_COLORS.border}`,
            marginBottom: EMZI_SPACING.lg
          }}
        >
          <CardHeader style={{ padding: EMZI_SPACING.lg }}>
            <CardTitle style={{
              fontSize: '16px',
              fontWeight: '600',
              color: EMZI_COLORS.textPrimary
            }}>
              {getText('platformsLabel')} *
            </CardTitle>
            <p style={{
              fontSize: '12px',
              color: EMZI_COLORS.textMuted,
              marginTop: EMZI_SPACING.xs
            }}>
              {getText('platformsHint')}
            </p>
          </CardHeader>
          <CardContent style={{ padding: EMZI_SPACING.lg, paddingTop: 0 }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: EMZI_SPACING.md
            }}>
              {PLATFORMS.map(platform => {
                const isSelected = selectedChannels.includes(platform.id);
                return (
                  <button
                    key={platform.id}
                    onClick={() => toggleChannel(platform.id)}
                    style={{
                      padding: EMZI_SPACING.lg,
                      borderRadius: '10px',
                      border: isSelected 
                        ? `3px solid #F89B29`
                        : 'none',
                      backgroundColor: EMZI_COLORS.primary,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: EMZI_SPACING.sm,
                      position: 'relative',
                      minHeight: '120px',
                      boxShadow: isSelected 
                        ? '0 0 20px rgba(248, 155, 41, 0.4)'
                        : 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                      if (!isSelected) {
                        e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      if (!isSelected) {
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >
                    <div style={{ color: 'white', opacity: isSelected ? 1 : 0.9 }}>
                      {platform.icon}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '700',
                      color: 'white'
                    }}>
                      {platform.id}
                    </div>
                    {isSelected && (
                      <div style={{
                        position: 'absolute',
                        top: EMZI_SPACING.sm,
                        right: EMZI_SPACING.sm,
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: '#F89B29',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="white">
                          <path d="M11.67 3.87L5.54 10 2.33 6.79" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            
            {errors.channels && (
              <p style={{ 
                color: EMZI_COLORS.error,
                fontSize: '12px',
                marginTop: EMZI_SPACING.sm
              }}>
                ⚠️ {errors.channels}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Duration Slider Card */}
        <Card 
          className="mb-4"
          style={{
            backgroundColor: EMZI_COLORS.background,
            borderRadius: EMZI_RADIUS.md,
            boxShadow: EMZI_SHADOWS.card,
            border: `1px solid ${EMZI_COLORS.border}`,
            marginBottom: EMZI_SPACING.lg
          }}
        >
          <CardHeader style={{ padding: EMZI_SPACING.lg }}>
            <CardTitle style={{
              fontSize: '16px',
              fontWeight: '600',
              color: EMZI_COLORS.textPrimary
            }}>
              {getText('durationLabel')} *
            </CardTitle>
            <p style={{
              fontSize: '12px',
              color: EMZI_COLORS.textMuted,
              marginTop: EMZI_SPACING.xs
            }}>
              {getText('durationHint')}
            </p>
          </CardHeader>
          <CardContent style={{ padding: EMZI_SPACING.lg, paddingTop: 0 }}>
            {/* Slider with label */}
            <div style={{ marginBottom: EMZI_SPACING.md }}>
              <div style={{
                textAlign: 'center',
                marginBottom: EMZI_SPACING.sm,
                fontSize: '32px',
                fontWeight: '700',
                color: EMZI_COLORS.primary
              }}>
                {formatDuration(durationSeconds)}
              </div>
              
              <input
                type="range"
                min="5"
                max="180"
                step="5"
                value={durationSeconds}
                onChange={handleSliderChange}
                style={{
                  width: '100%',
                  height: '8px',
                  borderRadius: '4px',
                  outline: 'none',
                  background: `linear-gradient(to right, ${EMZI_COLORS.primary} 0%, ${EMZI_COLORS.primary} ${((durationSeconds - 5) / 175) * 100}%, #E5E7EB ${((durationSeconds - 5) / 175) * 100}%, #E5E7EB 100%)`,
                  cursor: 'pointer',
                  WebkitAppearance: 'none',
                  appearance: 'none'
                }}
              />
              
              <style>{`
                input[type="range"]::-webkit-slider-thumb {
                  -webkit-appearance: none;
                  appearance: none;
                  width: 24px;
                  height: 24px;
                  border-radius: 50%;
                  background: ${EMZI_COLORS.primary};
                  cursor: pointer;
                  box-shadow: 0 2px 8px rgba(0, 102, 204, 0.4);
                  border: 3px solid white;
                }
                
                input[type="range"]::-moz-range-thumb {
                  width: 24px;
                  height: 24px;
                  border-radius: 50%;
                  background: ${EMZI_COLORS.primary};
                  cursor: pointer;
                  box-shadow: 0 2px 8px rgba(0, 102, 204, 0.4);
                  border: 3px solid white;
                }
              `}</style>
            </div>

            {/* Quick chips */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: EMZI_SPACING.sm,
              marginBottom: EMZI_SPACING.md
            }}>
              {QUICK_CHIPS.map(chip => (
                <button
                  key={chip.seconds}
                  onClick={() => handleQuickChip(chip.seconds)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: EMZI_RADIUS.sm,
                    border: durationSeconds === chip.seconds 
                      ? `2px solid ${EMZI_COLORS.primary}`
                      : `1px solid ${EMZI_COLORS.border}`,
                    backgroundColor: durationSeconds === chip.seconds 
                      ? EMZI_COLORS.primary
                      : EMZI_COLORS.background,
                    color: durationSeconds === chip.seconds 
                      ? 'white'
                      : EMZI_COLORS.textPrimary,
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (durationSeconds !== chip.seconds) {
                      e.currentTarget.style.borderColor = EMZI_COLORS.primary;
                      e.currentTarget.style.backgroundColor = '#EFF6FF';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (durationSeconds !== chip.seconds) {
                      e.currentTarget.style.borderColor = EMZI_COLORS.border;
                      e.currentTarget.style.backgroundColor = EMZI_COLORS.background;
                    }
                  }}
                >
                  {chip.label}
                </button>
              ))}
            </div>

            {/* Apply button */}
            <Button
              onClick={handleApply}
              disabled={selectedChannels.length === 0}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#F59E0B',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                border: 'none',
                borderRadius: EMZI_RADIUS.md,
                cursor: selectedChannels.length === 0 ? 'not-allowed' : 'pointer',
                opacity: selectedChannels.length === 0 ? 0.5 : 1,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (selectedChannels.length > 0) {
                  e.currentTarget.style.backgroundColor = '#D97706';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedChannels.length > 0) {
                  e.currentTarget.style.backgroundColor = '#F59E0B';
                  e.currentTarget.style.boxShadow = 'none';
                }
              }}
            >
              {getText('applyButton')}
            </Button>
          </CardContent>
        </Card>

        {/* Compatibility Badge */}
        {selectedChannels.length > 0 && (
          <Card 
            className="mb-4"
            style={{
              backgroundColor: EMZI_COLORS.background,
              borderRadius: EMZI_RADIUS.md,
              boxShadow: EMZI_SHADOWS.card,
              border: `1px solid ${EMZI_COLORS.border}`,
              marginBottom: EMZI_SPACING.lg
            }}
          >
            <CardContent style={{ padding: EMZI_SPACING.lg }}>
              <div style={{
                fontSize: '13px',
                color: EMZI_COLORS.textSecondary,
                marginBottom: EMZI_SPACING.xs
              }}>
                {getText('compatibilityTitle')}
              </div>
              
              {compatibilityInfo.compatible.length > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: EMZI_SPACING.xs,
                  marginBottom: compatibilityInfo.incompatible.length > 0 ? EMZI_SPACING.sm : 0
                }}>
                  <span style={{ color: '#10B981', fontSize: '16px' }}>✅</span>
                  <span style={{ fontSize: '13px', color: EMZI_COLORS.textPrimary }}>
                    {getText('compatibleWith')}: {compatibilityInfo.compatible.join(', ')}
                  </span>
                </div>
              )}
              
              {compatibilityInfo.incompatible.length > 0 && (
                <div style={{
                  padding: EMZI_SPACING.sm,
                  borderRadius: EMZI_RADIUS.sm,
                  backgroundColor: '#FEF3C7',
                  border: '1px solid #F59E0B'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: EMZI_SPACING.xs,
                    marginBottom: EMZI_SPACING.xs
                  }}>
                    <span style={{ color: '#F59E0B', fontSize: '16px' }}>⚠️</span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#92400E' }}>
                      {getText('incompatibleWith')}:
                    </span>
                  </div>
                  {compatibilityInfo.incompatible.map((item, idx) => (
                    <div key={`item-${idx}`} style={{ fontSize: '12px', color: '#92400E', marginLeft: '24px' }}>
                      • {item.platform}: {item.issue}. {item.suggestion}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* AI Tips Display (only after Apply) */}
        {aiTips && (
          <Card 
            className="mb-4"
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: EMZI_RADIUS.md,
              border: `2px solid #F59E0B`,
              marginBottom: EMZI_SPACING.lg,
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.15)'
            }}
          >
            <CardHeader style={{ padding: EMZI_SPACING.lg, paddingBottom: EMZI_SPACING.sm }}>
              <CardTitle style={{
                fontSize: '17px',
                fontWeight: '700',
                color: '#F59E0B',
                display: 'flex',
                alignItems: 'center',
                gap: EMZI_SPACING.sm
              }}>
                🤖 {getText('aiTipsTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent style={{ padding: EMZI_SPACING.lg, paddingTop: 0 }}>
              {/* Tips */}
              {aiTips.tips && aiTips.tips.length > 0 && (
                <div style={{ marginBottom: EMZI_SPACING.md }}>
                  {aiTips.tips.map((tip, idx) => (
                    <div 
                      key={`item-${idx}`}
                      style={{
                        padding: EMZI_SPACING.sm,
                        borderRadius: EMZI_RADIUS.sm,
                        backgroundColor: '#F0FDF4',
                        border: '1px solid #4ADE80',
                        marginBottom: idx < aiTips.tips.length - 1 ? EMZI_SPACING.sm : 0,
                        fontSize: '13px',
                        color: EMZI_COLORS.textSecondary,
                        lineHeight: '1.6'
                      }}
                    >
                      💡 {tip}
                    </div>
                  ))}
                </div>
              )}

              {/* Warnings */}
              {aiTips.warnings && aiTips.warnings.length > 0 && (
                <div style={{ marginBottom: EMZI_SPACING.md }}>
                  {aiTips.warnings.map((warning, idx) => (
                    <div 
                      key={`item-${idx}`}
                      style={{
                        padding: EMZI_SPACING.sm,
                        borderRadius: EMZI_RADIUS.sm,
                        backgroundColor: '#FFF3E0',
                        border: '1px solid #F97316',
                        marginBottom: idx < aiTips.warnings.length - 1 ? EMZI_SPACING.sm : 0
                      }}
                    >
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#9A3412' }}>
                        ⚠️ {warning.platform}: {warning.issue}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9A3412', marginTop: EMZI_SPACING.xs }}>
                        {warning.suggestion}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Ideal range */}
              {aiTips.ideal_range && (
                <div style={{
                  padding: EMZI_SPACING.sm,
                  borderRadius: EMZI_RADIUS.sm,
                  backgroundColor: '#F3F4F6',
                  fontSize: '12px',
                  color: EMZI_COLORS.textMuted,
                  textAlign: 'center'
                }}>
                  💡 Ideal range for your platforms: {aiTips.ideal_range[0]}–{aiTips.ideal_range[1]}s
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Submit Error */}
        {errors.submit && (
          <Card 
            className="mb-4"
            style={{
              backgroundColor: '#FFEBEE',
              borderRadius: EMZI_RADIUS.md,
              border: `1px solid ${EMZI_COLORS.error}`,
              marginBottom: EMZI_SPACING.lg
            }}
          >
            <CardContent style={{ padding: EMZI_SPACING.lg }}>
              <p style={{ 
                color: EMZI_COLORS.error,
                fontSize: '14px'
              }}>
                ❌ {errors.submit}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'flex-end',
          gap: EMZI_SPACING.md
        }}>
          <Button
            onClick={handleNext}
            disabled={selectedChannels.length === 0}
            style={{
              ...EMZI_BUTTON_STYLES.primary,
              opacity: selectedChannels.length === 0 ? 0.5 : 1,
              cursor: selectedChannels.length === 0 ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => {
              if (selectedChannels.length > 0) {
                e.target.style.backgroundColor = EMZI_COLORS.primaryHover;
                e.target.style.boxShadow = EMZI_SHADOWS.buttonHover;
              }
            }}
            onMouseLeave={(e) => {
              if (selectedChannels.length > 0) {
                e.target.style.backgroundColor = EMZI_COLORS.primary;
                e.target.style.boxShadow = EMZI_SHADOWS.button;
              }
            }}
          >
            {getText('nextButton')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Step0PlatformDuration;
